import { useForm } from "react-hook-form"
import { FleContinueWrapper, FleLayout } from "../FleLayout"
import { BackArrow, ContinueArrow, SwitchH2ToBodyLarge } from "../Molecules"
import { HelperText, Spacer } from "../../atoms/Atoms"
import { CompleteUsaStateSelect } from "components/views/atoms/forms/FormUsaStateSelect"
import React, { useState, useLayoutEffect, useEffect } from "react"
import L10n from "../../../../lib/carrot-l10n"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { Form } from "components/views/atoms/forms/Form"
import { TopSection } from "../TopSection"
import useAboutYou from "../hooks/useAboutYou"
import { useHistory } from "react-router"
import { useCurrentUser } from "components/context/user/UserContext"
import { useAboutYouFlowControls } from "../context/AboutYouFlowContext"
import { InternationalAddress } from "../../../InternationalAddress"
import { AboutYouTopSection } from "components/carrot-plans/shared/ProgressTracker"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const AboutYouAddress = ({ next, previous, onSkip }) => {
  const history = useHistory()
  const { register, handleSubmit, errors } = useForm()
  const { isUsa } = useCurrentUser()

  const { goBack, wentBack } = useAboutYouFlowControls()
  const { data, updateAboutYou, continueLoading } = useAboutYou()

  const [address1, setAddress1] = useState(data?.address?.address1 || "")
  const [address2, setAddress2] = useState(data?.address?.address2 || "")
  const [city, setCity] = useState(data?.address?.city || "")
  const [state, setState] = useState(data?.address?.state || "")
  const [zip, setZip] = useState(data?.address?.zip || "")
  const [nonUsaAddress, setNonUsaAddress] = useState(data?.nonUsaAddress || "")

  useEffect(() => {
    setAddress1(data?.address?.address1)
    setAddress2(data?.address?.address2)
    setCity(data?.address?.city)
    setState(data?.address?.state)
    setZip(data?.address?.zip)
    setNonUsaAddress(data?.nonUsaAddress)
  }, [
    data?.address?.address1,
    data?.address?.address2,
    data?.address?.city,
    data?.address?.state,
    data?.address?.zip,
    data?.nonUsaAddress
  ])

  useLayoutEffect(() => {
    if (!wentBack && data?.address.address1) {
      onSkip && onSkip()
      history.push(next)
    }
  }, [history, wentBack, next, data?.address.address1])

  async function onComplete(data) {
    await updateAboutYou(data)
    history.push(next)
  }

  const isValidAddress = () =>
    isUsa ? Boolean(address1) && Boolean(city) && Boolean(state) && Boolean(zip) : Boolean(nonUsaAddress)

  return (
    <FleLayout
      closeButton
      bottomContent={
        <FleContinueWrapper>
          <ContinueArrow onClick={handleSubmit(onComplete)} enabled={isValidAddress() && !continueLoading} />
        </FleContinueWrapper>
      }
    >
      <Form onSubmit={isValidAddress() && handleSubmit(onComplete)}>
        <AboutYouTopSection previous={previous}>
          <TopSection progressStep={2} leftOrBackArrow={<BackArrow onClick={() => goBack(previous)} />} />
        </AboutYouTopSection>
        <SwitchH2ToBodyLarge>{l`What's your home address?`}</SwitchH2ToBodyLarge>
        <Spacer height={0.25} />
        <HelperText color="black-80">{l`Your address helps us recommend provider and care options.`}</HelperText>
        <Spacer height={3} />
        {isUsa ? (
          <>
            <CompleteFormInput
              name={"address1"}
              label={"Address Line 1"}
              value={address1}
              errors={errors}
              ref={register}
              onChange={(e) => {
                setAddress1(e.target.value)
              }}
            />
            <CompleteFormInput
              name={"address2"}
              label={"Address Line 2 (Optional)"}
              value={address2}
              errors={errors}
              onChange={(e) => setAddress2(e.target.value)}
              ref={register}
            />
            <CompleteFormInput
              name={"city"}
              label={"City"}
              value={city}
              errors={errors}
              ref={register}
              onChange={(e) => {
                setCity(e.target.value)
              }}
            />
            <CompleteUsaStateSelect
              name={"state"}
              label={"State"}
              value={state}
              errors={errors}
              ref={register}
              onChange={(e) => {
                setState(e.target.value)
              }}
            />
            <CompleteFormInput
              name={"zip"}
              label={"Zipcode"}
              value={zip}
              errors={errors}
              ref={register({
                pattern: {
                  value: /^\d{5}$/,
                  message: "Zipcode must be 5 digits long"
                }
              })}
              onChange={(e) => {
                setZip(e.target.value)
              }}
            />
          </>
        ) : (
          <InternationalAddress
            label={"Home Address"}
            value={nonUsaAddress}
            nonUsaAddress={nonUsaAddress}
            register={register}
            errors={errors}
            onChange={setNonUsaAddress}
          />
        )}
      </Form>
    </FleLayout>
  )
}

export { AboutYouAddress }
